import React, { useState, useEffect } from 'react';
import { User, Crown, Pencil, Trash2, Search, X } from 'lucide-react';

// Reusable Modal component remains the same
const Modal = ({ show, title, message, onConfirm, onCancel, confirmText = 'Confirm' }) => {
    if (!show) return null;
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="modal-close" onClick={onCancel} aria-label="Close modal">
                        <X size={18} />
                    </button>
                </div>
                <p className="modal-message">{message}</p>
                <div className="modal-buttons">
                    <button onClick={onCancel} className="modal-button-cancel">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="modal-button-confirm">
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

// UserCard component updated for disabled user view
const UserCard = ({ user, onEdit, onToggleStatus, onDelete }) => {
    const isEnabled = user.status === 'Enable';
    const userIcon = user.type === 'ADMIN' ? <Crown size={16} /> : <User size={16} />;
    const statusText = isEnabled ? 'Enabled' : 'Disabled';
    const statusClass = isEnabled ? 'status-enabled' : 'status-disabled';
    const toggleButtonText = isEnabled ? 'Disable' : 'Enable';
    const toggleButtonClass = isEnabled ? 'toggle-disable' : 'toggle-enable';

    return (
        <div className={`user-card-container ${!isEnabled ? 'disabled-card' : ''}`}>
            <div className="user-card-details">
                <div className={`user-icon-bg ${user.type === 'ADMIN' ? 'admin' : 'user'}`}>
                    {userIcon}
                </div>
                <div className="user-info-text">
                    <h4 className="user-name">{user.name}</h4>
                    <span className="user-type">{user.type}</span>
                </div>
            </div>

            <div className="user-status-section">
                <span className={`status-dot ${statusClass}`}></span>
                <span className="status-label">{statusText}</span>
            </div>

            <div className="user-actions">
                <button
                    onClick={() => onEdit(user)}
                    className="action-button edit-button"
                    title="Edit User"
                >
                    <Pencil size={18} />
                </button>
                <button
                    onClick={() => onToggleStatus(user)}
                    className={`action-button toggle-button ${toggleButtonClass}`}
                    title={toggleButtonText + " User"}
                >
                    {toggleButtonText}
                </button>
                <button
                    onClick={() => onDelete(user.id)}
                    className="action-button delete-button"
                    title="Delete User"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        </div>
    );
};

const DisabledUserPage = () => {
    // Initial data including both enabled and disabled users
    const [allUsers, setAllUsers] = useState([
        { id: 1, type: 'ADMIN', name: 'ADMIN', password: '9009230000#Abhi', status: 'Enable' },
        { id: 2, type: 'USER', name: 'BHARTI', password: '968530', status: 'Enable' },
        { id: 3, type: 'USER', name: 'HARMEET KAUR', password: '73892', status: 'Disable' },
        { id: 4, type: 'USER', name: 'DIVYA SONI', password: 'Abhi#1234', status: 'Disable' },
        { id: 5, type: 'USER', name: 'RAGINI BARSE', password: '139300', status: 'Disable' },
        { id: 6, type: 'USER', name: 'ROSHAN', password: '7771867176', status: 'Disable' },
        { id: 7, type: 'USER', name: 'HUMESH', password: 'Abhi#1234', status: 'Disable' },
    ]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({});

    // Filter to only show disabled users and store them in their own state
    const disabledUsers = allUsers.filter(user => user.status === 'Disable');
    const [displayedUsers, setDisplayedUsers] = useState(disabledUsers);

    // Update the displayed users whenever the original list of allUsers changes
    useEffect(() => {
        setDisplayedUsers(allUsers.filter(user => user.status === 'Disable'));
    }, [allUsers]);

    const handleSearch = () => {
        const filteredList = allUsers.filter(user =>
            user.status === 'Disable' &&
            (user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.type.toLowerCase().includes(searchQuery.toLowerCase()))
        );
        // Set the new state with the filtered list
        setDisplayedUsers(filteredList);
    };

    const handleReset = () => {
        setAllUsers([
            { id: 1, type: 'ADMIN', name: 'ADMIN', password: '9009230000#Abhi', status: 'Enable' },
            { id: 2, type: 'USER', name: 'BHARTI', password: '968530', status: 'Enable' },
            { id: 3, type: 'USER', name: 'HARMEET KAUR', password: '73892', status: 'Disable' },
            { id: 4, type: 'USER', name: 'DIVYA SONI', password: 'Abhi#1234', status: 'Disable' },
            { id: 5, type: 'USER', name: 'RAGINI BARSE', password: '139300', status: 'Disable' },
            { id: 6, type: 'USER', name: 'ROSHAN', password: '7771867176', status: 'Disable' },
            { id: 7, type: 'USER', name: 'HUMESH', password: 'Abhi#1234', status: 'Disable' },
        ]);
        // Clear the search input
        setSearchQuery('');
        // Reset the displayed users to the full list of disabled users
        setDisplayedUsers(allUsers.filter(user => user.status === 'Disable'));
    };

    const handleEdit = (user) => {
        setModalConfig({
            title: 'Edit User',
            message: `You are about to edit user: ${user.name}.`,
            onConfirm: () => setShowModal(false),
            onCancel: () => setShowModal(false),
            confirmText: 'Continue'
        });
        setShowModal(true);
    };

    const handleToggleStatus = (user) => {
        setAllUsers(allUsers.map(u =>
            u.id === user.id ? { ...u, status: u.status === 'Enable' ? 'Disable' : 'Enable' } : u
        ));
    };

    const handleDelete = (id) => {
        setModalConfig({
            title: 'Delete User',
            message: 'Are you sure you want to delete this user? This action cannot be undone.',
            onConfirm: () => {
                setAllUsers(allUsers.filter(user => user.id !== id));
                setShowModal(false);
            },
            onCancel: () => setShowModal(false),
            confirmText: 'Delete'
        });
        setShowModal(true);
    };

    return (
        <div className="user-dashboard">
            <style>{`
                /* Your original CSS styles from the previous response */
                :root {
                    --primary-color: #2fb2e7;
                    --secondary-color: #6c757d;
                    --success-color: #10b981;
                    --danger-color: #ef4444;
                    --bg-light: #f3f4f6;
                    --bg-card: #ffffff;
                    --text-dark: #1f2937;
                    --border-color: #d1d5db;
                }
                body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; background-color: var(--bg-light); }
                .user-dashboard { padding: 2rem; min-height: 100vh; }
                .main-header { background-color: #2fb2e7; color: white; padding: 1.5rem 2rem; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border-radius: 8px; margin-bottom: 2rem; }
                .header-title { font-size: 1.5rem; font-weight: 700; }
                .header-nav a, .header-nav span { color: #e5e7eb; text-decoration: none; font-size: 0.9rem; }
                .header-nav a:hover { text-decoration: underline; }
                .controls-section { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1.5rem; margin-bottom: 1.5rem; }
                .search-input-group { flex-grow: 1; position: relative; }
                .search-input-group input { width: 100%; padding: 0.75rem 1rem 0.75rem 3rem; border: 1px solid var(--border-color); border-radius: 8px; font-size: 1rem; box-shadow: inset 0 1px 2px rgba(0,0,0,0.05); }
                .search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: var(--secondary-color); }
                .action-buttons { display: flex; gap: 0.75rem; }
                .btn { padding: 0.75rem 1.5rem; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap; font-size: 0.9rem; }
                .btn-primary { background-color: var(--primary-color); color: white; }
                .btn-primary:hover { background-color: #2563eb; }
                .btn-secondary { background-color: var(--bg-card); color: var(--text-dark); border: 1px solid var(--border-color); }
                .btn-secondary:hover { background-color: var(--bg-light);color: var(--text-dark); }
                .btn-add { background-color: var(--success-color); color: white; }
                .btn-add:hover { background-color: #065f46; }
                .user-list { display: grid; gap: 1.5rem; }
                @media (min-width: 768px) { .user-list { grid-template-columns: repeat(auto-fill, minmax(350px, 1fr)); } }
                .user-card-container { background-color: var(--bg-card); padding: 1.5rem; border-radius: 12px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); display: flex; flex-direction: column; gap: 1rem; border: 1px solid var(--border-color); transition: transform 0.2s; }
                .user-card-container:hover { transform: translateY(-5px); box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1); }
                .user-card-details { display: flex; align-items: center; gap: 1rem; }
                .user-icon-bg { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; }
                .user-icon-bg.admin { background-color: var(--success-color); }
                .user-icon-bg.user { background-color: var(--primary-color); }
                .user-info-text { display: flex; flex-direction: column; }
                .user-name { margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--text-dark); }
                .user-type { font-size: 0.875rem; color: var(--secondary-color); text-transform: uppercase; font-weight: 500; }
                .user-status-section { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; font-size: 0.9rem; padding-left: 0.25rem; }
                .status-dot { width: 10px; height: 10px; border-radius: 50%; }
                .status-dot.status-enabled { background-color: var(--success-color); }
                .status-dot.status-disabled { background-color: var(--danger-color); }
                .status-label { color: var(--secondary-color); }
                .status-enabled .status-label { color: var(--success-color); }
                .status-disabled .status-label { color: var(--danger-color); }
                .user-actions { display: flex; gap: 0.75rem; justify-content: flex-end; padding-top: 1rem; border-top: 1px dashed var(--border-color); }
                .action-button { padding: 0.5rem 1rem; border: none; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; font-weight: 500; transition: background-color 0.2s; }
                .edit-button { background-color: #e0f2fe; color: #0369a1; }
                .edit-button:hover { background-color: #dbeafe; }
                .delete-button { background-color: #fee2e2; color: #dc2626; }
                .delete-button:hover { background-color: #fecaca; }
                .toggle-button { font-size: 0.85rem; font-weight: 600; color: white; padding: 0.5rem 1rem; }
                .toggle-enable { background-color: var(--success-color); }
                .toggle-enable:hover { background-color: #065f46; }
                .toggle-disable { background-color: var(--danger-color); }
                .toggle-disable:hover { background-color: #b91c1c; }
                .modal-overlay { position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.4); display: flex; justify-content: center; align-items: center; z-index: 1000; animation: fadeIn 0.3s; }
                .modal-content { background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2); max-width: 400px; width: 90%; animation: slideUp 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 1rem; margin-bottom: 1rem; }
                .modal-title { font-size: 1.25rem; font-weight: 700; margin: 0; }
                .modal-close { background: none; border: none; cursor: pointer; color: var(--secondary-color); }
                .modal-close:hover { color: var(--text-dark); }
                .modal-message { color: var(--secondary-color); margin-bottom: 1.5rem; }
                .modal-buttons { display: flex; justify-content: flex-end; gap: 0.75rem; }
                .modal-button-cancel { background: var(--bg-light); color: var(--text-dark); }
                .modal-button-confirm { background: var(--danger-color); color: white; }

                /* Styles for the disabled user page */
                .disabled-card {
                    opacity: 0.85;
                    background-color: #ffffffff;
                    border-left: 5px solid var(--danger-color);
                }
                .disabled-card:hover {
                    transform: none;
                    box-shadow: none;
                }
                
                /* New media query for desktop view */
                @media (min-width: 1024px) {
                    .user-dashboard {
                        margin-left: 236px;
                    }
                }
            `}</style>

            <header className="main-header">
                <h1 className="header-title">Disabled Users - List</h1>
                <nav className="header-nav">
                    <a href="DashBoard">Home</a>
                    <span>/</span>
                    <span>Disabled Users - List</span>
                </nav>
            </header>

            <main className="main-content">
                <div className="controls-section">
                    <div className="search-input-group">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name or type..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            // Add a keydown event handler for 'Enter' key
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                    </div>
                    <div className="action-buttons">
                        <button onClick={handleSearch} className="btn btn-primary">
                            Search
                        </button>
                        <button onClick={handleReset} className="btn btn-secondary">
                            Reset
                        </button>
                    </div>
                </div>

                <div className="user-list">
                    {displayedUsers.length > 0 ? (
                        displayedUsers.map((user) => (
                            <UserCard
                                key={user.id}
                                user={user}
                                onEdit={handleEdit}
                                onToggleStatus={handleToggleStatus}
                                onDelete={handleDelete}
                            />
                        ))
                    ) : (
                        <p className="text-center text-gray-500">No disabled users found.</p>
                    )}
                </div>
            </main>

            <Modal
                show={showModal}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={modalConfig.onCancel}
                confirmText={modalConfig.confirmText}
            />
        </div>
    );
};

export default DisabledUserPage;